import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface UserData {
  profile: {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
  };
  subscription: {
    plan: string;
    status: string;
    trialEndsAt: Date;
    billingCycle?: string;
  };
  hotelCode: string;
  ownedHotels: string[];
  hotelAdmin: {
    username: string;
    createdAt: Date;
  };
}

const Products = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/');
        return;
      }

      setUser(currentUser);
      
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'webUsers', currentUser.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data() as UserData;
          setUserData(data);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const getDaysRemaining = () => {
    if (!userData?.subscription?.trialEndsAt) return 0;
    const trialEnd = userData.subscription.trialEndsAt instanceof Date ? 
      userData.subscription.trialEndsAt : 
      new Date(userData.subscription.trialEndsAt);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!user || !userData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Unable to load your product information. Please try logging in again.
        </Alert>
      </Container>
    );
  }

  const daysRemaining = getDaysRemaining();
  const isTrialActive = userData.subscription.plan === 'trial' && daysRemaining > 0;
  const trialExpired = userData.subscription.plan === 'trial' && daysRemaining === 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Products
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your Sespresso subscription and hotel services
        </Typography>
      </Box>

      {/* Subscription Status */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Sespresso Pro
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Chip
                      label={userData.subscription.plan === 'trial' ? 'Free Trial' : 'Pro Plan'}
                      color={userData.subscription.status === 'active' ? 'success' : 'default'}
                    />
                    <Chip
                      label={userData.subscription.status}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h4" color="primary">
                    {userData.subscription.plan === 'trial' ? '$0' : '$99'}
                    <Typography component="span" variant="body2" color="text.secondary">
                      /month
                    </Typography>
                  </Typography>
                </Box>
              </Box>

              {/* Trial Status */}
              {isTrialActive && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your free trial expires in {daysRemaining} days. 
                  <Button size="small" sx={{ ml: 1 }}>
                    Upgrade Now
                  </Button>
                </Alert>
              )}

              {trialExpired && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Your free trial has expired. Please upgrade to continue using Sespresso.
                  <Button size="small" sx={{ ml: 1 }}>
                    Upgrade Now
                  </Button>
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Plan Features */}
              <Typography variant="h6" gutterBottom>
                Plan Features
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ Multi-Zone Audio Control
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ Event Scheduling
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ Professional Audio Support
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ Cloud Management
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ Mobile Access
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✅ 24/7 Support
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Hotel Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hotel Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Primary Hotel Code
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: '0.1em', color: 'primary.main' }}>
                  {userData.hotelCode}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Hotel Name
                </Typography>
                <Typography variant="body1">
                  {userData.profile.company || 'Hotel'}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Hotels
                </Typography>
                <Typography variant="body1">
                  {userData.ownedHotels?.length || 0}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.open('https://app.rafilis.com', '_blank')}
              >
                Open Hotel Control Panel
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Account */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hotel Admin Account
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Admin Username
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {userData.hotelAdmin?.username || 'Not set'}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {userData.hotelAdmin?.createdAt ? 
                    new Date(userData.hotelAdmin.createdAt).toLocaleDateString() : 
                    'Not available'
                  }
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label="Active"
                  color="success"
                  size="small"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Use these credentials to login to your hotel control panel
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => window.open('https://app.rafilis.com', '_blank')}
                  >
                    Control Panel
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/billing')}
                  >
                    Billing
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/account')}
                  >
                    Account Settings
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled
                  >
                    Download App
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Products;